// API base URL — resolved from environment variable injected at build time.
// Set REACT_APP_API_BASE_URL in the deployment environment (AWS Amplify /
// CloudFront / S3 build pipeline, etc.) to point at your backend.
// Falls back to localhost for local development.
const RAW_BASE = process.env.REACT_APP_API_BASE_URL;

export const API_BASE =
  RAW_BASE ||
  (process.env.NODE_ENV === "development" ? "http://localhost:3001" : "");

function joinUrl(base, path) {
  if (!base) return path;
  return `${base.replace(/\/+$/, "")}/${String(path).replace(/^\/+/, "")}`;
}

function appendParams(url, params) {
  if (!params || typeof params !== "object") return url;
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null) continue;
    usp.append(k, String(v));
  }
  const qs = usp.toString();
  if (!qs) return url;
  return url.includes("?") ? `${url}&${qs}` : `${url}?${qs}`;
}

/** Read the JWT token from localStorage (stored by App.js on login) */
function getAuthToken() {
  try {
    return localStorage.getItem("authToken") || "";
  } catch { /* ignore */ }
  return "";
}

/** Read the logged-in username from localStorage (kept for audit/display purposes) */
function getUsername() {
  try {
    const u = localStorage.getItem("user");
    if (u) return JSON.parse(u)?.username || "";
  } catch { /* ignore */ }
  return "";
}

const REQUEST_TIMEOUT_MS = 30_000; // 30 秒超时

async function request(method, path, data, config = {}) {
  let url = joinUrl(API_BASE, path);
  url = appendParams(url, config.params);

  const token    = getAuthToken();
  const username = getUsername();
  const headers  = { ...(config.headers || {}) };

  // Primary auth: JWT Bearer token
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  // Keep x-username for server-side audit logging (non-security, convenience only)
  if (username) {
    headers["x-username"] = username;
  }

  let body = undefined;

  if (data instanceof FormData) {
    body = data;
  } else if (data !== undefined && data !== null && method !== "GET") {
    headers["Content-Type"] = headers["Content-Type"] || "application/json";
    body = JSON.stringify(data);
  }

  // 30 秒超时：网络卡住时中止请求，避免 loading 永不停止
  const controller = new AbortController();
  const timeoutId  = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  let res;
  try {
    res = await fetch(url, {
      method,
      headers,
      credentials: "include",
      body,
      signal: controller.signal,
    });
  } catch (err) {
    if (err.name === "AbortError") {
      const timeoutErr = new Error("请求超时，请检查网络连接后重试");
      timeoutErr.response = { status: 408, data: { error: "Request timeout" } };
      throw timeoutErr;
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }

  const contentType = res.headers.get("content-type") || "";
  const text = await res.text();

  const parsed =
    contentType.includes("application/json") ? (text ? JSON.parse(text) : null) : text;

  if (!res.ok) {
    // If 401, the token is expired/invalid — clear it and signal App.js to show login modal
    if (res.status === 401) {
      localStorage.removeItem("authToken");
      localStorage.removeItem("user");
      // Dispatch a custom event — App.js listens and opens the login modal (no page reload)
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("auth:logout"));
      }
    }
    // Throw an axios-compatible error so callers can use err?.response?.status
    // and err?.response?.data?.error like the rest of the codebase expects.
    const err = new Error(`HTTP ${res.status}`);
    err.response = {
      status: res.status,
      data: parsed || { error: text },
      headers: Object.fromEntries(res.headers.entries()),
    };
    throw err;
  }

  return {
    data: parsed,
    status: res.status,
    headers: Object.fromEntries(res.headers.entries()),
  };
}

const api = {
  get:    (path, config)        => request("GET",    path, undefined, config),
  post:   (path, data, config)  => request("POST",   path, data,      config),
  put:    (path, data, config)  => request("PUT",    path, data,      config),
  patch:  (path, data, config)  => request("PATCH",  path, data,      config),
  delete: (path, config)        => request("DELETE", path, undefined, config),
};

export default api;
